import { useEffect, useRef, type ReactNode } from 'react';
import { Stack, TextInput } from '@mantine/core';
import { emergencyErrors, type EmergencyDraft } from '@/lib/driverStandards';
import { useCopy, type RequirementLevel } from '@/lib/settings';
import { Callout, PhoneInput } from '@/components/ui';
import '@/styles/member.css';

interface Props {
  value: EmergencyDraft;
  onChange: (next: EmergencyDraft) => void;
  /** 'required' marks the fields required; 'notAsked' renders nothing. */
  level: RequirementLevel;
  /** Anchor id for deep links / scroll-into-view. */
  id?: string;
  /** The API said it is missing (409/400 emergency_contact_required): show the reason and scroll here. */
  highlight?: boolean;
  disabled?: boolean;
  /** Extra content under the fields (e.g. a Save button on Account). */
  footer?: ReactNode;
  /** No card of its own (the page already puts it inside a SectionCard). */
  plain?: boolean;
}

/** Name, phone number and relationship of the person to call in an emergency (copy key emergency.intro). */
export function EmergencyContactCard({ value, onChange, level, id, highlight, disabled, footer, plain }: Props) {
  const { t } = useCopy();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [highlight]);
  if (level === 'notAsked') return null;

  const required = level === 'required';
  const errors = emergencyErrors(value, level, highlight);
  const set = (patch: Partial<EmergencyDraft>) => onChange({ ...value, ...patch });

  return (
    <div
      className={plain ? undefined : 'rides-card'}
      id={id}
      ref={ref}
      style={{ padding: plain ? 0 : 16, scrollMarginTop: 80 }}
    >
      <Stack gap="md">
        <div>
          <p className="rm-muted" style={{ color: 'var(--rides-ink)' }}>{t('emergency.intro')}</p>
          {!required && <p className="rm-small" style={{ marginTop: 4 }}>{t('emergency_card.optional')}</p>}
        </div>
        {highlight && <Callout tone="warn">{t('emergency.required')}</Callout>}
        <TextInput
          label="Their name"
          placeholder="Thandi Mokoena"
          autoComplete="off"
          maxLength={150}
          value={value.name}
          onChange={(e) => set({ name: e.currentTarget.value })}
          required={required}
          disabled={disabled}
          error={errors.name}
        />
        <PhoneInput
          label="Their cellphone number"
          autoComplete="off"
          maxLength={30}
          value={value.phone}
          onChange={(phone) => set({ phone })}
          required={required}
          disabled={disabled}
          error={errors.phone}
        />
        <TextInput
          label="How you know them"
          description="Optional"
          placeholder="Spouse, parent or friend"
          autoComplete="off"
          maxLength={50}
          value={value.relationship}
          onChange={(e) => set({ relationship: e.currentTarget.value })}
          disabled={disabled}
        />
        {footer}
      </Stack>
    </div>
  );
}
